import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { AttachmentsGrid } from "@/components/scribe/attachments-grid";
import { NoteAnswersSection } from "@/components/scribe/note-answers-section";
import SourceDocuments from "@/components/source-documents";
import { Button, Divider } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import NoteActionsBar from "@/components/ui/note-actions-bar";
import { formatDateTime } from "@/lib/format-data";
import { getProviderNames } from "@/lib/get-provider-names";
import { getNoteTitleFallback, hasScribeAttachmentsQuestions } from "@/lib/note-utils";
import { NoteListItem } from "@/types/notes";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
interface NoteViewDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  note: NoteListItem | null;
}
export default function NoteViewDialog(t0) {
  const $ = _c(49);
  const {
    isOpen,
    onOpenChange,
    note
  } = t0;
  const closeButtonRef = useRef(null);
  note?.providers;
  let t1;
  if ($[0] !== note?.providers) {
    t1 = note?.providers || [];
    $[0] = note?.providers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = getProviderNames(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const providerNames = t2;
  let t3;
  if ($[4] !== note?.id) {
    t3 = noteQueryOptions.detail(note?.id || "");
    $[4] = note?.id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = isOpen && !!note?.id && note.hasAnswers;
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      ...t3,
      enabled: t4
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: noteDetail,
    isPending: isLoadingNoteDetail
  } = useQuery(t5);
  noteDetail?.template;
  note?.hasAnswers;
  let t6;
  bb0: {
    if (note?.hasAnswers && isLoadingNoteDetail) {
      t6 = true;
      break bb0;
    }
    const t7 = noteDetail?.template ?? null;
    let t8;
    if ($[9] !== t7) {
      t8 = hasScribeAttachmentsQuestions(t7);
      $[9] = t7;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    t6 = t8;
  }
  const templateHandlesAttachments = t6;
  let t7;
  if ($[11] !== note?.id) {
    t7 = noteQueryOptions.attachments(note?.id ?? "");
    $[11] = note?.id;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isOpen && !!note?.id && !templateHandlesAttachments;
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = {
      ...t7,
      enabled: t8
    };
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: t10,
    isPending: isLoadingAttachments
  } = useQuery(t9);
  let t11;
  if ($[16] !== t10) {
    t11 = t10 === undefined ? [] : t10;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const standaloneAttachments = t11;
  let t12;
  if ($[18] !== standaloneAttachments) {
    t12 = standaloneAttachments.map(_temp);
    $[18] = standaloneAttachments;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const gridAttachments = t12;
  const displayNote = noteDetail || note;
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e => {
      e.preventDefault();
      closeButtonRef.current?.focus();
    };
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== note) {
    t14 = note ? getNoteTitleFallback(note) : "Note Details";
    $[21] = note;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <DialogTitle>{t14}</DialogTitle>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== displayNote || $[26] !== note?.hasAnswers) {
    t16 = note?.hasAnswers && displayNote && <NoteActionsBar noteItem={displayNote} type="note" />;
    $[25] = displayNote;
    $[26] = note?.hasAnswers;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t15 || $[29] !== t16) {
    t17 = <DialogHeader><div className="flex items-center justify-between">{t15}{t16}</div></DialogHeader>;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== gridAttachments || $[32] !== isLoadingAttachments || $[33] !== isLoadingNoteDetail || $[34] !== note || $[35] !== noteDetail || $[36] !== providerNames || $[37] !== templateHandlesAttachments) {
    t18 = <DialogBody>{isLoadingNoteDetail && note?.hasAnswers ? <div className="flex items-center justify-center py-8"><div className="text-body text-muted-foreground">Loading note details...</div></div> : <><div className="space-y-1 text-body text-foreground">{note?.visitDate && <div><span className="font-medium">Visit: </span>{formatDateTime(note.visitDate)}</div>}{note?.createdDate && note?.visitDate && new Date(note.createdDate).getTime() !== new Date(note.visitDate).getTime() && <div><span className="font-medium">Created: </span>{formatDateTime(note.createdDate)}</div>}{note?.lastEditedDate && note?.visitDate && new Date(note.lastEditedDate).getTime() !== new Date(note.visitDate).getTime() && <div><span className="font-medium">Edited: </span>{formatDateTime(note.lastEditedDate)}</div>}<div><span className="font-medium">Contributors: </span>{providerNames}</div></div>{note?.summary && <><Divider className="my-4" /><div className="max-w-none space-y-1"><ReactMarkdown components={{
              h1: _temp2,
              h2: _temp3,
              h3: _temp4,
              ul: _temp5,
              li: _temp6
            } as any}>{note.summary}</ReactMarkdown></div></>}{!templateHandlesAttachments && gridAttachments.length > 0 && note?.id && <><Divider className="my-4" /><div><h3 className="mb-2 text-body-strong text-muted-foreground">Attachments</h3><AttachmentsGrid attachments={gridAttachments} isLoading={isLoadingAttachments} size="lg" groupByDate={false} showUpload={false} patientId={note.patient?.id} noteId={note.id} appointmentId={note.eventId} showNoteLink={false} /></div></>}{noteDetail && noteDetail.template && noteDetail.answers && noteDetail.answers.length > 0 && <><Divider className="my-4" /><NoteAnswersSection note={noteDetail} isEditing={false} /></>}{note?.sourceDocuments && note.sourceDocuments.length > 0 && <><Divider className="my-4" /><SourceDocuments sourceDocuments={note.sourceDocuments} /></>}</>}</DialogBody>;
    $[31] = gridAttachments;
    $[32] = isLoadingAttachments;
    $[33] = isLoadingNoteDetail;
    $[34] = note;
    $[35] = noteDetail;
    $[36] = providerNames;
    $[37] = templateHandlesAttachments;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== onOpenChange) {
    t19 = <DialogFooter><Button variant="secondary" ref={closeButtonRef} onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>;
    $[39] = onOpenChange;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t17 || $[42] !== t18 || $[43] !== t19) {
    t20 = <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto" onOpenAutoFocus={t13}>{t17}{t18}{t19}</DialogContent>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== isOpen || $[46] !== onOpenChange || $[47] !== t20) {
    t21 = <Dialog open={isOpen} onOpenChange={onOpenChange}>{t20}</Dialog>;
    $[45] = isOpen;
    $[46] = onOpenChange;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  return t21;
}
function _temp6(t0) {
  const {
    children: children_3
  } = t0;
  return <li className="text-body text-foreground-secondary">{children_3}</li>;
}
function _temp5(t0) {
  const {
    children: children_2
  } = t0;
  return <ul className="list-disc pl-5">{children_2}</ul>;
}
function _temp4(t0) {
  const {
    children: children_1
  } = t0;
  return <p className="whitespace-pre-wrap">{children_1}</p>;
}
function _temp3(t0) {
  const {
    children: children_0
  } = t0;
  return <h3 className="pt-2">{children_0}</h3>;
}
function _temp2(t0) {
  const {
    children
  } = t0;
  return <h2 className="text-h1">{children}</h2>;
}
function _temp(a) {
  return {
    id: a.id,
    attachmentId: a.id,
    url: a.signedUrl || "",
    signedUrl: a.signedUrl,
    thumbnailSignedUrl: a.thumbnailSignedUrl,
    gcsPath: a.gcsPath,
    annotations: a.annotations,
    name: a.name,
    date: a.createdDate,
    uploadedAt: a.uploadedAt,
    tags: a.tags
  };
}